import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from './ui/button';
import {
  CheckCircle, Circle, X, ArrowRight, Rocket, CaretDown, CaretUp
} from '@phosphor-icons/react';

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await API.get('/admin/onboarding/status');
      setData(res.data);
    } catch {
      setData(null);
    }
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await API.post('/admin/onboarding/dismiss');
      setData(prev => ({ ...prev, dismissed: true }));
    } catch {}
    finally { setDismissing(false); }
  };

  if (!data || data.dismissed) return null;

  const { steps, completed, total } = data;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const allDone = completed === total;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="onboarding-checklist">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <Rocket size={18} weight="duotone" className="text-orange-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-navy-900">
              {allDone ? 'Setup Complete!' : 'Get Started with IAG Platform'}
            </h3>
            <p className="text-xs text-navy-500 mt-0.5">
              {allDone
                ? 'All steps completed. You can dismiss this checklist.'
                : `${completed} of ${total} steps completed`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Progress */}
          <div className="flex items-center gap-2 mr-2">
            <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-orange-500"
                style={{ width: `${pct}%` }}
                data-testid="onboarding-progress-bar"
              />
            </div>
            <span className="text-xs font-bold text-navy-700" data-testid="onboarding-progress-text">{pct}%</span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
            data-testid="onboarding-toggle"
          >
            {expanded ? <CaretUp size={14} className="text-navy-400" /> : <CaretDown size={14} className="text-navy-400" />}
          </button>
          <button
            onClick={handleDismiss}
            disabled={dismissing}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
            data-testid="onboarding-dismiss"
            title="Dismiss checklist"
          >
            <X size={14} className="text-navy-400" />
          </button>
        </div>
      </div>

      {/* Steps */}
      {expanded && (
        <div className="border-t border-slate-100 divide-y divide-slate-50" data-testid="onboarding-steps">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className={`flex items-center gap-4 px-5 py-3 transition-colors ${step.completed ? 'bg-emerald-50/30' : 'hover:bg-slate-50 cursor-pointer'}`}
              onClick={() => !step.completed && navigate(step.link)}
              data-testid={`onboarding-step-${step.id}`}
            >
              {/* Status Icon */}
              <div className="shrink-0">
                {step.completed ? (
                  <CheckCircle size={22} weight="fill" className="text-emerald-500" />
                ) : (
                  <div className="w-[22px] h-[22px] rounded-full border-2 border-slate-300 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-navy-400">{idx + 1}</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${step.completed ? 'text-emerald-700 line-through' : 'text-navy-900'}`}>
                  {step.title}
                </p>
                <p className={`text-xs mt-0.5 ${step.completed ? 'text-emerald-500' : 'text-navy-400'}`}>
                  {step.description}
                </p>
              </div>

              {/* Action */}
              {!step.completed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 text-xs border-orange-200 text-orange-700 hover:bg-orange-50"
                  onClick={(e) => { e.stopPropagation(); navigate(step.link); }}
                  data-testid={`onboarding-action-${step.id}`}
                >
                  Go <ArrowRight size={12} className="ml-1" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
